import { createContext, useContext, useMemo, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "./translations";
import { langFromPath, counterpartPath } from "./route";

const LanguageContext = createContext(null);

// localStorage key kept for backwards compatibility (users may have visited
// before). We READ it only to remember the last switcher click in this session
// for analytics, but we NEVER let it decide what language to render.
const STORAGE_KEY = "ghio.lang";
const SUPPORTED = ["nl", "en"];

/**
 * The URL is the ONLY source of truth for the active language.
 *
 * Rules (see spec):
 *  - `lang` is derived from `location.pathname`. A crawler on / always gets
 *    Dutch; a crawler on /en/ always gets English. No navigator.language,
 *    no localStorage, no Accept-Language, no geolocation, no auto-redirect.
 *  - Clicking the NL/EN switcher NAVIGATES to the counterpart URL rather
 *    than flipping in-memory state. The URL change flips the language.
 *  - localStorage is written for analytics/UX only (nostalgia value) and is
 *    NEVER read to decide rendering.
 */
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(location.pathname);

  // Keep <html lang> and dir accurate for screen readers and Google.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback(
    (next) => {
      if (!SUPPORTED.includes(next)) return;
      // Remember the user's last explicit choice — analytics only, never
      // consulted for rendering.
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* private mode */
      }
      const target = counterpartPath(location.pathname, next) +
        (location.search || "") +
        (location.hash || "");
      if (target !== location.pathname + (location.search || "") + (location.hash || "")) {
        navigate(target);
      }
    },
    [location.pathname, location.search, location.hash, navigate],
  );

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations.nl;
      const segments = key.split(".");
      let value = dict;
      for (const s of segments) {
        value = value?.[s];
        if (value === undefined) break;
      }
      if (value === undefined) {
        let fb = translations.nl;
        for (const s of segments) fb = fb?.[s];
        return fb ?? key;
      }
      return value;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be inside LanguageProvider");
  return ctx;
};
